import { z } from "zod";
import { Id, IsoDate } from "./common.ts";

/**
 * Calendar intelligence (ALP-73): national days, holidays, religious dates, international days and crises,
 * filtered per org (with a per-account override) and overlaid on the content calendar.
 */

/** "global" or an ISO 3166-1 alpha-2 country code. */
export const CalendarScope = z.string().regex(/^(global|[A-Z]{2})$/, "global or an ISO 3166-1 alpha-2 code");
export const CalendarEventType = z.enum(["public_holiday", "national_day", "religious", "observance", "international_day", "crisis", "global_event"]);
export const CalendarMood = z.enum(["celebratory", "neutral", "solemn", "sensitive"]);
export const CalendarSourceId = z.enum(["nager", "curated", "hijri", "reliefweb", "gdelt", "brave"]);
export const YmdDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");
export const CalendarSeverity = z.enum(["low", "medium", "high", "critical"]);

const Localized = z.object({ en: z.string(), ar: z.string() });
const GuidanceNotes = z.object({ do: z.array(z.string()), dont: z.array(z.string()), ideas: z.array(z.string()) });

/** AI-written content guidance for a day: tone, do/don't notes, post ideas and hashtags (both languages). */
export const ContentGuidance = z.object({
  tone: z.enum(["celebratory", "warm", "respectful", "informative", "silent"]),
  en: GuidanceNotes,
  ar: GuidanceNotes,
  hashtags: z.array(z.string()),
});

export const CalendarEvent = z.object({
  id: Id,
  scope: CalendarScope,
  type: CalendarEventType,
  startDate: YmdDate,
  endDate: YmdDate, // inclusive
  allDay: z.boolean(),
  title: Localized,
  description: z.object({ en: z.string().nullable(), ar: z.string().nullable() }),
  mood: CalendarMood,
  source: CalendarSourceId,
  /** Every source that reported this event (after de-duplication). */
  sources: z.array(CalendarSourceId),
  sourceRef: z.string(),
  sourceUrl: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  /** Hijri-based dates depend on moon sighting: ±1 day. */
  approximate: z.boolean(),
  /** Hijri date of the first day for religious days, e.g. { en: "12 Rabi al-Awwal 1448 AH", ar: "12 ربيع الأول 1448 هـ" }. */
  hijri: z.object({ en: z.string(), ar: z.string() }).nullable().optional(),
  status: z.enum(["published", "needs_review"]),
  severity: CalendarSeverity.nullable(),
  guidance: ContentGuidance.nullable(),
  /** This org's decision: dismissed (hidden) or approved (a needs_review crisis the org confirmed). */
  orgState: z.enum(["dismissed", "approved"]).nullable(),
  fetchedAt: IsoDate,
});

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");
export const CalendarSubscriptionInput = z.object({
  countries: z.array(CalendarScope).max(40),
  categories: z.array(CalendarEventType),
  /** Moods shown by default. */
  moods: z.array(CalendarMood),
  /** Per-country mood choice ("happy or sad" per country); missing country = `moods`. */
  moodsByCountry: z.record(CalendarScope, z.array(CalendarMood)).default({}),
  crisisAlerts: z.boolean(),
  reminderLeadDays: z.array(z.number().int().min(0).max(60)).max(5),
  quietHours: z.object({ from: HHMM, to: HHMM, tz: z.string().min(1).max(64) }).nullable(),
});
export const CalendarSubscription = CalendarSubscriptionInput.extend({
  accountId: Id.nullable(),
  /** true when an account has no override and inherits the org subscription. */
  inherited: z.boolean(),
  updatedAt: IsoDate.nullable(),
});
export const UpdateCalendarSubscriptionRequest = CalendarSubscriptionInput.extend({ accountId: Id.nullable().default(null) });

export const CalendarPreviewRequest = z.object({
  subscription: CalendarSubscriptionInput,
  from: YmdDate.optional(), // default: today (UTC)
  days: z.number().int().min(7).max(366).default(90),
});
export const CalendarPreview = z.object({
  from: YmdDate,
  to: YmdDate,
  total: z.number(),
  months: z.array(z.object({ month: z.string().regex(/^\d{4}-\d{2}$/), counts: z.record(CalendarMood, z.number()), events: z.array(CalendarEvent) })),
});

export const CalendarCountry = z.object({
  code: CalendarScope,
  name: Localized,
  flag: z.string(),
  region: z.enum(["global", "gulf", "levant", "north_africa", "other"]),
  timezone: z.string(),
  sources: z.array(CalendarSourceId),
});
export const CalendarCatalog = z.object({ countries: z.array(CalendarCountry), categories: z.array(CalendarEventType), moods: z.array(CalendarMood) });

export const CalendarConflict = z.object({
  publicationId: Id,
  postId: Id,
  accountId: Id,
  scheduledAt: IsoDate,
  caption: z.string(),
  event: CalendarEvent,
  severity: z.enum(["warning", "critical"]),
  suggestion: z.enum(["pause", "reschedule", "review_tone"]),
  /** First free day after the event window, same local time (null when no reschedule is suggested). */
  suggestedAt: IsoDate.nullable(),
});

export const CalendarSourceHealth = z.object({
  source: CalendarSourceId,
  enabled: z.boolean(),
  status: z.enum(["ok", "failing", "disabled", "never_ran"]),
  lastRunAt: IsoDate.nullable(),
  lastSuccessAt: IsoDate.nullable(),
  lastError: z.string().nullable(),
  consecutiveFailures: z.number(),
  lastCount: z.number(),
  note: z.string().nullable(),
});
export const CalendarRun = z.object({
  id: Id,
  trigger: z.enum(["schedule", "manual"]),
  status: z.enum(["running", "succeeded", "partial", "failed"]),
  startedAt: IsoDate,
  finishedAt: IsoDate.nullable(),
  added: z.number(),
  changed: z.number(),
  removed: z.number(),
  ai: z.object({ processed: z.number(), cached: z.number(), failed: z.number() }),
  sources: z.record(z.string(), z.object({ ok: z.boolean(), count: z.number(), error: z.string().nullable(), ms: z.number() })),
  /** Sample of what changed today (capped). */
  changes: z.array(z.object({ change: z.enum(["added", "changed", "removed"]), source: CalendarSourceId, sourceRef: z.string(), title: z.string(), date: YmdDate })),
});
export const CalendarHealth = z.object({ sources: z.array(CalendarSourceHealth), runs: z.array(CalendarRun) });

/** Per-user in-app notification preferences for calendar categories. */
export const CalendarNotificationPrefs = z.object({
  reminders: z.boolean(),
  crisis: z.boolean(),
  conflicts: z.boolean(),
  digest: z.enum(["off", "daily", "weekly"]),
});

export const CalendarEventsQuery = z.object({
  from: YmdDate,
  to: YmdDate,
  accountId: Id.optional(),
  /** "true" includes events this org dismissed (shown greyed out). Not a boolean: "false" would coerce to true. */
  includeDismissed: z.enum(["true", "false"]).optional(),
});

export type CalendarEvent = z.infer<typeof CalendarEvent>;
export type CalendarEventType = z.infer<typeof CalendarEventType>;
export type CalendarMood = z.infer<typeof CalendarMood>;
export type CalendarSourceId = z.infer<typeof CalendarSourceId>;
export type CalendarSubscriptionInput = z.infer<typeof CalendarSubscriptionInput>;
export type CalendarSubscription = z.infer<typeof CalendarSubscription>;
export type CalendarPreview = z.infer<typeof CalendarPreview>;
export type CalendarCatalog = z.infer<typeof CalendarCatalog>;
export type CalendarCountry = z.infer<typeof CalendarCountry>;
export type CalendarConflict = z.infer<typeof CalendarConflict>;
export type CalendarSourceHealth = z.infer<typeof CalendarSourceHealth>;
export type CalendarRun = z.infer<typeof CalendarRun>;
export type CalendarHealth = z.infer<typeof CalendarHealth>;
export type CalendarNotificationPrefs = z.infer<typeof CalendarNotificationPrefs>;
export type ContentGuidance = z.infer<typeof ContentGuidance>;
